import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {Configuration} from 'configuration';
import type {SearchSchemas} from 'api/search.dto.generated';
import {createBaseQueryArgs} from 'store/api/createBaseQueryArgs';
import type {Agent} from '../agent/types';
import type {Collection} from '../series/types';
import type {
  AgentSearchProps,
  AgentWorksSearchProps,
  AgentsCountResult,
  AgentsCountSearchProps,
  SearchCountResultDto,
  SearchResult,
  SearchResultDto,
  SeriesCountResult,
  SeriesCountSearchProps,
  SeriesSearchProps,
  WorkHit,
  WorkSearchProps,
} from './types';
import {
  mapAgentHits,
  mapAgentSearchPropsToSearchProps,
  mapAgentWorksSearchPropsToSearchProps,
  mapAgentsCountResponseDto,
  mapAgentsCountSearchPropsToSearchProps,
  mapCollectionHits,
  mapSeriesCountResponseDto,
  mapSeriesCountSearchPropsToSearchProps,
  mapSeriesSearchPropsToSearchProps,
  mapToElasticCloudAuthLambda,
  mapWorkHits,
  mapWorkSearchPropsToSearchProps,
} from './mappers';

const baseElasticApi = createApi({
  endpoints: () => ({}),
  reducerPath: 'elasticApi',
  baseQuery: fetchBaseQuery(
    createBaseQueryArgs(Configuration.searchApi.elastic.uri),
  ),
});

export const elasticApi = baseElasticApi.injectEndpoints({
  endpoints: build => ({
    searchAgent: build.query<
      SearchResult<Agent>,
      AgentSearchProps,
      SearchResultDto<SearchSchemas.AgentDocument>
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapAgentHits,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapAgentSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
    searchSeries: build.query<
      SearchResult<Collection>,
      SeriesSearchProps,
      SearchResultDto<SearchSchemas.SeriesDocument>
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapCollectionHits,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapSeriesSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
    searchWork: build.query<
      SearchResult<WorkHit>,
      WorkSearchProps,
      SearchResultDto<SearchSchemas.WorkDocument>
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapWorkHits,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapWorkSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
    searchAgentWorks: build.query<
      SearchResult<WorkHit>,
      AgentWorksSearchProps,
      SearchResultDto<SearchSchemas.WorkDocument>
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapWorkHits,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapAgentWorksSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
    searchAgentsCount: build.query<
      AgentsCountResult,
      AgentsCountSearchProps,
      SearchCountResultDto
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapAgentsCountResponseDto,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapAgentsCountSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
    searchSeriesCount: build.query<
      SeriesCountResult,
      SeriesCountSearchProps,
      SearchCountResultDto
    >({
      keepUnusedDataFor: 60,
      transformResponse: mapSeriesCountResponseDto,
      query: ({abortSignal, ...props}) => ({
        url: '',
        body: mapToElasticCloudAuthLambda(
          mapSeriesCountSearchPropsToSearchProps(props),
        ),
        method: 'POST',
        signal: abortSignal,
      }),
    }),
  }),
});

export const {
  useLazySearchAgentQuery,
  useLazySearchSeriesQuery,
  useLazySearchWorkQuery,
  useLazySearchAgentWorksQuery,
  useLazySearchAgentsCountQuery,
  useLazySearchSeriesCountQuery,
} = elasticApi;
